import { z } from 'zod'
import { decimalSchema, positiveMoneySchema, timestampSchema, uuidSchema } from './common.ts'
import {
  commercialManualExchangeRateInputSchema,
  commercialOperationTypeSchema,
} from './pricing.ts'
import { unitSchema } from './units.ts'
import { isoCurrencySchema } from './warehouse.ts'

/** Matches the payment metrics the BRD §4.7 dashboard has to report. */
export const INVOICE_STATUSES = ['draft', 'issued', 'awaiting', 'paid', 'overdue'] as const

export const invoiceStatusSchema = z.enum(INVOICE_STATUSES)
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Чернетка',
  issued: 'Виставлено',
  awaiting: 'Очікує оплати',
  paid: 'Оплачено',
  overdue: 'Прострочено',
}

/** Accounting's sign-off on a draft. Issuing requires `approved`; approval is final. */
export const INVOICE_APPROVAL_STATUSES = ['not_reviewed', 'approved', 'rejected'] as const
export const invoiceApprovalStatusSchema = z.enum(INVOICE_APPROVAL_STATUSES)
export type InvoiceApprovalStatus = z.infer<typeof invoiceApprovalStatusSchema>
export const INVOICE_APPROVAL_STATUS_LABELS: Record<InvoiceApprovalStatus, string> = {
  not_reviewed: 'Не погоджено',
  approved: 'Погоджено',
  rejected: 'Відхилено',
}

export const invoiceSchema = z.object({
  id: uuidSchema,
  number: z.string().min(1),
  clientId: uuidSchema,
  orderId: uuidSchema.nullable(),
  dealId: uuidSchema.nullable(),
  sellerEntityId: uuidSchema.nullable(),
  sellerName: z.string().nullable(),
  sellerEdrpou: z.string().nullable(),
  status: invoiceStatusSchema,
  approvalStatus: invoiceApprovalStatusSchema,
  amount: positiveMoneySchema,
  netAmount: decimalSchema,
  vatAmount: decimalSchema,
  currencyCode: isoCurrencySchema,
  vatTreatmentCode: z.string().nullable(),
  vatTreatmentName: z.string().nullable(),
  vatRate: decimalSchema.nullable(),
  pricesIncludeVat: z.boolean().nullable(),
  operationType: commercialOperationTypeSchema,
  counterpartyCountryCode: z.string().nullable(),
  exchangeRateSnapshotId: uuidSchema.nullable(),
  requestedRateDate: z.iso.date(),
  effectiveRateDate: z.iso.date(),
  exchangeRate: decimalSchema,
  exchangeRateUnits: decimalSchema,
  exchangeRateProvider: z.string(),
  exchangeRateSourceUrl: z.string(),
  exchangeRateManualReason: z.string().nullable(),
  exchangeRateManualBy: z.string().nullable(),
  uahEquivalent: decimalSchema,
  issuedAt: timestampSchema.nullable(),
  dueAt: timestampSchema.nullable(),
  paidAt: timestampSchema.nullable(),
  createdBy: z.string().min(1),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})

export type Invoice = z.infer<typeof invoiceSchema>

export const createInvoiceInputSchema = z
  .object({
    clientId: uuidSchema,
    orderId: uuidSchema.nullable(),
    dealId: uuidSchema.nullable().optional(),
    sellerEntityId: uuidSchema.optional(),
    amount: positiveMoneySchema,
    currencyCode: isoCurrencySchema.default('UAH'),
    vatTreatmentId: uuidSchema.nullable().default(null),
    operationType: commercialOperationTypeSchema.default('domestic'),
    counterpartyCountryCode: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/)
      .nullable()
      .default(null),
    rateDate: z.iso.date().optional(),
    manualRate: commercialManualExchangeRateInputSchema.optional(),
    status: invoiceStatusSchema.default('draft'),
    issuedAt: timestampSchema.nullable(),
    dueAt: timestampSchema.nullable(),
  })
  .superRefine((input, context) => {
    if (input.operationType === 'export' && !input.counterpartyCountryCode) {
      context.addIssue({
        code: 'custom',
        path: ['counterpartyCountryCode'],
        message: 'Для експорту вкажіть країну контрагента',
      })
    }
    if (input.currencyCode === 'UAH' && input.manualRate) {
      context.addIssue({ code: 'custom', path: ['manualRate'], message: 'Для UAH курс завжди 1:1' })
    }
  })

export type CreateInvoiceInput = z.input<typeof createInvoiceInputSchema>

export const updateInvoiceInputSchema = z
  .object({
    orderId: uuidSchema.nullable().optional(),
    amount: positiveMoneySchema.optional(),
    status: invoiceStatusSchema.optional(),
    issuedAt: timestampSchema.nullable().optional(),
    dueAt: timestampSchema.nullable().optional(),
    paidAt: timestampSchema.nullable().optional(),
  })
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateInvoiceInput = z.infer<typeof updateInvoiceInputSchema>

export const invoiceLineItemSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  position: decimalSchema,
  description: z.string(),
  quantity: decimalSchema,
  unit: unitSchema,
  unitPrice: decimalSchema,
  discountPercent: decimalSchema,
  netAmount: decimalSchema,
  vatAmount: decimalSchema,
  totalAmount: decimalSchema,
  createdAt: timestampSchema,
})

export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>

export const updateInvoiceApprovalInputSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('approved'),
    comment: z.string().trim().min(1).max(2000).optional(),
  }),
  z.object({ status: z.literal('rejected'), reason: z.string().trim().min(1).max(2000) }),
])
export type UpdateInvoiceApprovalInput = z.infer<typeof updateInvoiceApprovalInputSchema>

export const invoiceApprovalEntrySchema = z.object({
  id: uuidSchema,
  fromStatus: invoiceApprovalStatusSchema,
  toStatus: invoiceApprovalStatusSchema,
  reason: z.string().nullable(),
  comment: z.string().nullable(),
  changedBy: z.string(),
  changedByName: z.string().nullable(),
  changedAt: timestampSchema,
})
export type InvoiceApprovalEntry = z.infer<typeof invoiceApprovalEntrySchema>

export const createInvoiceCommentInputSchema = z.object({
  body: z.string().trim().min(1).max(2000),
})
export type CreateInvoiceCommentInput = z.infer<typeof createInvoiceCommentInputSchema>

export const invoiceCommentSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  body: z.string(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: timestampSchema,
})
export type InvoiceComment = z.infer<typeof invoiceCommentSchema>
